/**
 * JobCardPerMechanicHours — iter138.9y → iter174
 * ─────────────────────────────────────────────────────────────────────────
 * Compact stacked rows showing how much EACH mechanic contributed to this
 * Job Card. Renders whenever the JC has been touched by more than one
 * mechanic OR the only contributor is NOT the current assignee (i.e. the
 * card was re-assigned away). This guarantees a re-assigned mechanic's
 * effort NEVER disappears from the card — even before the new mechanic has
 * clocked any time.
 *
 * Per user (option c): show PRODUCED (actual clocked time) + COMPLETED
 * (sum of quoted_labour_hours for tasks that mechanic ticked done).
 * Everything is derived client-side from `card.time_log` + `card.line_items`
 * so tiles stay fetch-free.
 *
 * Visual (per mechanic chip):  "Alice · 1h 24m · ✓1.5h"   (amber pulse = live)
 */
import { useEffect, useState, useMemo } from 'react';
import { Wrench, CheckCircle2 } from 'lucide-react';

const fmtHM = (totalMin) => {
  if (totalMin == null || isNaN(totalMin)) return '0m';
  const m = Math.max(0, Math.round(totalMin));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem.toString().padStart(2, '0')}m`;
};

const fmtH = (hours) => {
  const h = Number(hours) || 0;
  if (h <= 0) return '0h';
  return h % 1 === 0 ? `${h}h` : `${h.toFixed(2).replace(/0$/, '')}h`;
};

export default function JobCardPerMechanicHours({ card, mechanics = [] }) {
  const [_tick, setTick] = useState(0);
  const timeLog = card?.time_log || [];
  const lineItems = card?.line_items || [];
  const currentAssignee = card?.assigned_to || null;

  // Tick every minute so any live row updates without remounting
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const perMechanic = useMemo(() => {
    const now = Date.now();
    // mechanic_id -> { produced, isLive, completed }
    const buckets = new Map();
    const ensure = (mid) => {
      if (!buckets.has(mid)) buckets.set(mid, { produced: 0, isLive: false, completed: 0 });
      return buckets.get(mid);
    };

    // 1) PRODUCED — sum session durations from the time_log per mechanic.
    for (const s of timeLog) {
      const mid = s.mechanic_id;
      if (!mid || !s.started_at) continue;
      const start = new Date(s.started_at).getTime();
      const isLive = !s.ended_at;
      const end = isLive ? now : new Date(s.ended_at).getTime();
      const mins = Math.max(0, (end - start) / 60000);
      const cur = ensure(mid);
      cur.produced += mins;
      if (isLive) cur.isLive = true;
    }

    // 2) COMPLETED — sum quoted_labour_hours of tasks each mechanic ticked
    //    done (completed_by_id wins; completed_by is the legacy alias).
    for (const li of lineItems) {
      if (!li?.completed) continue;
      const mid = li.completed_by_id || li.completed_by;
      if (!mid) continue;
      ensure(mid).completed += Number(li.quoted_labour_hours) || 0;
    }

    return Array.from(buckets.entries()).map(([id, v]) => ({ id, ...v }));
     
  }, [timeLog, lineItems, _tick]);

  // Keep mechanics who actually contributed something (time or completed work).
  const contributors = perMechanic.filter(r => r.produced > 0 || r.completed > 0);

  // Render when 2+ mechanics contributed OR the sole contributor is NOT the
  // current assignee (re-assigned away → their effort must stay visible).
  const soloIsCurrentAssignee =
    contributors.length === 1 && contributors[0].id === currentAssignee;
  if (contributors.length === 0 || soloIsCurrentAssignee) return null;

  const nameOf = (id) => {
    const m = mechanics.find(u => u.user_id === id);
    return m?.name || m?.email || 'Mechanic';
  };

  // Highest producer first.
  const rows = contributors.slice().sort((a, b) => b.produced - a.produced);

  return (
    <div
      className="mt-1 flex flex-wrap gap-1"
      data-testid={`jc-per-mechanic-hours-${card.job_card_id}`}
    >
      {rows.map((r) => (
        <span
          key={r.id}
          className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${
            r.isLive
              ? 'bg-amber-50 border-amber-300 text-amber-800'
              : (r.id === currentAssignee
                  ? 'bg-slate-50 border-slate-200 text-slate-700'
                  : 'bg-indigo-50 border-indigo-200 text-indigo-800')
          }`}
          title={`${nameOf(r.id)} · produced ${fmtHM(r.produced)}${r.completed > 0 ? ` · completed ${fmtH(r.completed)}` : ''}${r.isLive ? ' (currently active)' : (r.id !== currentAssignee ? ' (previous mechanic)' : '')}`}
          data-testid={`jc-mech-row-${card.job_card_id}-${r.id}`}
        >
          {r.isLive && (
            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" aria-hidden />
          )}
          <Wrench className="h-2.5 w-2.5 opacity-70" />
          <strong className="font-semibold">{nameOf(r.id)}</strong>
          <span className="text-slate-400">·</span>
          <span className="tabular-nums" data-testid={`jc-mech-produced-${card.job_card_id}-${r.id}`}>{fmtHM(r.produced)}</span>
          {r.completed > 0 && (
            <>
              <CheckCircle2 className="h-2.5 w-2.5 text-emerald-600" />
              <span className="tabular-nums text-emerald-700" data-testid={`jc-mech-completed-${card.job_card_id}-${r.id}`}>{fmtH(r.completed)}</span>
            </>
          )}
        </span>
      ))}
    </div>
  );
}
